import { DeployFiltersError, SelectedFilter, StepIndexType } from '@shared/types';
import { useState, useCallback, Dispatch, SetStateAction } from 'react';

interface UseMultiStepDialogOptions {
    totalSteps: StepIndexType;
    initialFilter?: SelectedFilter;
    onValidateNavigation?: (filter: SelectedFilter) => boolean;
}

interface UseMultiStepDialogReturn {
    activeStepIndex: StepIndexType;
    setActiveStepIndex: Dispatch<SetStateAction<StepIndexType>>;
    isFirstStep: boolean;
    isLastStep: boolean;
    handleNext: () => void;
    handleBack: () => void;
    handleStep: (step: StepIndexType) => () => void;
    resetStep: () => void;
    selectedFilter: SelectedFilter;
    setSelectedFilter: Dispatch<SetStateAction<SelectedFilter>>;
    handleFilterChange: (value: SetStateAction<SelectedFilter>) => void;
    error: DeployFiltersError;
    setError: Dispatch<SetStateAction<DeployFiltersError>>;
    validateNavigation: () => boolean;
    skippedStepsIndexes: Set<StepIndexType>;
    setSkippedStepsIndexes: Dispatch<SetStateAction<Set<StepIndexType>>>;
    isStepSkipped: (step: StepIndexType) => boolean;
}

const defaultFilter: SelectedFilter = {
    branch: '',
    build: '',
    build_details: { architecture: '' },
    is_public: false,
};

const useMultiStepDialog = ({
    totalSteps,
    initialFilter = defaultFilter,
    onValidateNavigation,
}: UseMultiStepDialogOptions): UseMultiStepDialogReturn => {
    const [activeStepIndex, setActiveStepIndex] = useState(0);
    const [selectedFilter, setSelectedFilter] = useState<SelectedFilter>(initialFilter);
    const [error, setError] = useState<DeployFiltersError>(null);
    const [skippedStepsIndexes, setSkippedStepsIndexes] = useState(new Set<StepIndexType>());

    const isFirstStep = activeStepIndex === 0;
    const isLastStep = activeStepIndex === totalSteps - 1;

    const validateNavigation = useCallback(() => {
        if (onValidateNavigation) {
            return onValidateNavigation(selectedFilter);
        }

        if (!selectedFilter.branch) {
            setError('branch');
            return false;
        }
        if (!selectedFilter.build) {
            setError('build');
            return false;
        }
        if (selectedFilter.build_details?.architecture !== undefined && !selectedFilter.build_details.architecture) {
            setError('architecture');
            return false;
        }

        setError(null);
        return true;
    }, [selectedFilter, onValidateNavigation]);

    const handleNext = useCallback(() => {
        if (validateNavigation()) {
            setActiveStepIndex((prevActiveStep) => {
                const newStep = prevActiveStep + 1;

                if (skippedStepsIndexes.has(prevActiveStep)) {
                    const newSkipped = new Set(skippedStepsIndexes.values());
                    newSkipped.delete(prevActiveStep);
                    setSkippedStepsIndexes(newSkipped);
                }

                return Math.min(newStep, totalSteps - 1);
            });
        }
    }, [validateNavigation, totalSteps, skippedStepsIndexes]);

    const handleBack = useCallback(() => {
        setActiveStepIndex((prev) => Math.max(prev - 1, 0));
    }, []);

    const handleStep = useCallback(
        (step: StepIndexType) => () => {
            if (validateNavigation()) {
                setActiveStepIndex(step);
            }
        },
        [validateNavigation],
    );

    const handleFilterChange = useCallback((value: SetStateAction<SelectedFilter>) => {
        setError(null);
        setSelectedFilter(value);
    }, []);

    const resetStep = useCallback(() => {
        setActiveStepIndex(0);
    }, []);

    const isStepSkipped = useCallback(
        (step: StepIndexType) => {
            return skippedStepsIndexes.has(step);
        },
        [skippedStepsIndexes],
    );

    return {
        activeStepIndex,
        setActiveStepIndex,
        isFirstStep,
        isLastStep,
        handleNext,
        handleBack,
        handleStep,
        resetStep,
        selectedFilter,
        setSelectedFilter,
        handleFilterChange,
        error,
        setError,
        validateNavigation,
        skippedStepsIndexes,
        setSkippedStepsIndexes,
        isStepSkipped,
    };
};

export default useMultiStepDialog;
